import { 장례절차_LIST } from "@/containers/MatchContainer/constants/request";
import { formatPhoneNumber } from "@/utils/formatIdentifyNumber";
import { Box, Button, HStack, VStack, Input } from "@chakra-ui/react";
import { colors, customEvent, Field, Spacing, useResponsive } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { useCallback, useState } from "react";
import { PrevNext } from "./RequestQuestionStepByStep";
import { useSubmitLead } from "@/hooks/useSubmitLead";
import { mubinsoLeadStatus } from "@/containers/MatchContainer/components/MatchRequest/Mubinso/mubinsoConstants";
import { questionTemplate } from "@/containers/HomeContainer/remotes/homeQuestionTemplates";
import { useRecoilState } from "recoil";
import { matchRequestProcedureLoadingState } from "@/atoms/match/matchRequestLoadingState";
import router from "next/router";
import { postLeadConsentRecord } from "@/apis/leadConsentRecord";
import { useGetServerTime } from "@/hooks/useGetServerTime";

export default function RequestQuestionProcedureStepByStep({ onBack }: { onBack: () => void }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProcedure, setSelectedProcedure] = useState<string>("");

  const [value, setValue] = useState("");
  const [memo, setMemo] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const [loading, setLoading] = useRecoilState(matchRequestProcedureLoadingState);
  const { isBiggerThanLg } = useResponsive();

  const { data: serverTime } = useGetServerTime();
  const { submitLead } = useSubmitLead();
  const isNight = serverTime?.is_night ?? false;

  const handleClick = useCallback(async () => {
    if (loading) return;

    customEvent({
      newGtm: {
        path: "match_request",
        trigger_and_target: "procedure-submit",
      },
    });

    if (!value) {
      alert("번호를 입력해주세요.");
      return;
    }
    if (!memo) {
      alert("장례 준비 상황을 알려주세요.");
      return;
    }

    if (!/^010-\d{4}-\d{4}$/.test(value)) {
      alert("번호 형식을 확인해주세요. (010-****-****)");
      return;
    }

    const slackMessage = questionTemplate.procedure(selectedProcedure, memo);

    setLoading(true);
    try {
      await submitLead({
        phone: value,
        lead_type: "home",
        slack_message: slackMessage,
        status: mubinsoLeadStatus(memo),
        accept_night_call: isNight ? true : undefined,
      });
      customEvent({
        newGtm: {
          action: "match_request_procedure_submit_success",
        },
      });
      setIsCompleted(true);
    } catch (error) {
      console.error("전송 실패:", error);
      alert("잠시 오류가 발생했습니다. 문제가 지속될 시 고객센터(1666-9784)로 연락주세요.");
    } finally {
      setLoading(false);
    }
  }, [value, memo, selectedProcedure, loading, isNight, submitLead]);

  const handleMarketingAgree = useCallback(() => {
    customEvent({
      newGtm: {
        path: "match_request",
        trigger_and_target: "marketing_agree",
      },
    });
    postLeadConsentRecord({
      phone: value,
      agreement: { marketing_agreed: true },
    }).catch((e) => console.error("마케팅 수신동의 기록 실패:", e));
    router.push("/guidebook/?utm_campaign=match_request");
  }, [value]);

  const handleClose = useCallback(() => {
    customEvent({
      newGtm: {
        path: "match_request",
        trigger_and_target: "marketing_decline",
      },
    });
    router.push("/guidebook/?utm_campaign=match_request");
  }, []);

  if (currentStep === 2 && isCompleted) {
    return (
      <BaseStack
        width="100%"
        px={24}
        maxW="480px"
        m="0 auto"
        minH="300px"
        h="calc(50vh - 150px)"
        justifyContent="center"
      >
        <BaseStack width="100%" spacing={isBiggerThanLg ? 48 : 32}>
          <BaseStack width="100%" spacing={isBiggerThanLg ? 24 : 16} alignItems="center">
            <Box className="subtitle_21_b gray900" textAlign="center">
              신청이 완료되었어요
            </Box>
            <Box className="body_14_r gray700" textAlign="center" mt="8px">
              마케팅 정보 수신에 동의하시면
              <br />
              장례 관련 혜택, 이벤트 소식을 보내드려요.
            </Box>
          </BaseStack>
          <BaseStack
            direction="horizontal"
            spacing={isBiggerThanLg ? 24 : 16}
            alignItems="center"
            justifyContent="center"
          >
            <Box className="caption_12_b" color={colors.gray400}>
              선택
            </Box>
            <Box className="caption_12_b gray700">마케팅 정보 수신 동의</Box>
            <a
              className="caption_12_b"
              href="https://goifuneral.notion.site/3d74caff865d48ccbf32d9a0ad3df80b"
              target="_blank"
            >
              보기
            </a>
          </BaseStack>
          <BaseStack direction="horizontal" spacing={32} justifyContent="center">
            <Box flex={1}>
              <Button
                width="100%"
                backgroundColor={colors.gray100}
                color={colors.gray700}
                onClick={handleClose}
              >
                다음에
              </Button>
            </Box>
            <Box flex={1}>
              <Button
                width="100%"
                backgroundColor={colors.orange650}
                color={colors.white}
                onClick={handleMarketingAgree}
              >
                동의하기
              </Button>
            </Box>
          </BaseStack>
        </BaseStack>
      </BaseStack>
    );
  }

  if (currentStep === 2) {
    return (
      <VStack width="100%" padding="0 20px">
        <Spacing gap={60} />

        <Box width="100%" className="head2_b gray900" whiteSpace="pre-line">
          필요한 장례 정보 무료로 받기
        </Box>

        <VStack width="100%" alignItems="flex-start">
          <Box className="caption_12_b" whiteSpace="pre-line" textAlign="left">
            선택하신 정보 외에도, 필요한 사항을 모두 상세히 확인하실 수 있어요.
          </Box>

          <Spacing gap={28} />

          <HStack width="100%" justifyContent="center">
            <Input
              value={value}
              onChange={(e) => {
                const formatted = formatPhoneNumber(e.target.value);
                setValue(formatted);
              }}
              maxLength={13}
              border={`1px solid ${colors.gray400}`}
              width="100%"
              placeholder={"010-****-****"}
            />
          </HStack>

          <Spacing gap={20} />

          <Box>
            <Box className="gray700 subtitle_14_b" paddingBottom="12px">
              상황을 알려주세요(맞춤형 안내)
            </Box>
            <Field.Radio
              title=""
              items={[
                "미리 준비하려고 해요",
                "임종이 임박하신 상태입니다.",
                "임종하신 상태입니다.",
              ]}
              value={memo}
              onChange={(e) => {
                customEvent({
                  newGtm: {
                    path: "match_request",
                    trigger_and_target: "recommendation_memo-option",
                    text: e,
                  },
                });
                setMemo(e);
              }}
              radioWidth="100%"
              className="body_14_r"
              flexDirection="column"
              alignItem="center"
            />
          </Box>

          <Spacing gap={28} />

          <HStack width="100%" justifyContent="space-between" alignItems="center" spacing={0}>
            <HStack alignItems="center" gap={4}>
              <Box className="caption_12_b" color={colors.gray400}>
                필수
              </Box>
              <Box className="caption_12_b gray700">개인정보 수집, 이용, 제공 동의</Box>
            </HStack>
            <a
              className="caption_12_b"
              href="https://goifuneral.notion.site/146a79013e6c40a9b4731dd07a8ad99d"
              target="_blank"
            >
              보기
            </a>
          </HStack>
          {isNight && (
            <>
              <Spacing gap={8} />
              <HStack width="100%" justifyContent="space-between" alignItems="center" spacing={0}>
                <HStack alignItems="center" gap={4}>
                  <Box className="caption_12_b" color={colors.gray400}>
                    필수
                  </Box>
                  <Box className="caption_12_b gray700">긴급 상황 시 수신 동의(야간)</Box>
                </HStack>
                <a
                  className="caption_12_b"
                  href="https://goifuneral.notion.site/3e893cfdb8584fb28a4086a60aef5ad4"
                  target="_blank"
                >
                  보기
                </a>
              </HStack>
            </>
          )}

          <Spacing gap={16} />

          <Button
            width="100%"
            backgroundColor={colors.orange650}
            color={colors.white}
            onClick={handleClick}
            isLoading={loading}
            disabled={loading}
          >
            동의하고 자료 받아보기
          </Button>
        </VStack>

        <Spacing gap={360} />
      </VStack>
    );
  }

  return (
    <VStack px="16px">
      <Spacing gap={60} />

      <Box
        paddingTop="12px"
        width="100%"
        whiteSpace="pre-line"
        className="subtitle1_b black"
        paddingBottom="12px"
      >
        준비해야 할 것들이 많아서 걱정되시나요?
        <br />
        가장 먼저 알아보고 싶은 부분을
        <br />
        선택해주세요.
      </Box>

      <Box paddingTop="24px" width="100%">
        <VStack width="100%">
          <Field.ButtonGroups
            items={장례절차_LIST}
            value={selectedProcedure}
            onChange={(e) => {
              setSelectedProcedure(e);
              customEvent({
                newGtm: {
                  path: "match_request",
                  trigger_and_target: "procedure-answer",
                },
              });
            }}
            buttonWidth="100%"
            borderColor={colors.white}
          />
        </VStack>
      </Box>

      <Spacing gap={24} />

      <HStack
        paddingTop="4px"
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        gap="24px"
      >
        <Box width="100%">
          <PrevNext
            text="이전"
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "match_request",
                  trigger_and_target: "move-prev-procedure",
                },
              });
              onBack();
            }}
          />
        </Box>

        <Box width="100%">
          <PrevNext
            disabled={!selectedProcedure}
            text="다음"
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "match_request",
                  trigger_and_target: "move-next-procedure",
                },
              });

              setCurrentStep(2);

              customEvent({
                newGtm: {
                  action: "match_request_procedure_complete",
                },
              });
            }}
          />
        </Box>
      </HStack>

      <Spacing gap={360} />
    </VStack>
  );
}
